import { motorNaoTratado, provedorOpencodeNaoTratado, type Motor, type ProvedorOpencode } from '@remoto/protocolo';
import {
  nomeDoProvedorOpencode,
  provedorDoModelo,
  type ContaNanogpt,
  type JanelaDoPainel,
  type SessaoDoPainel,
  type UsoDaSessao,
} from '@remoto/shared';
import { porcento, tokensCurtos } from '../formato';

/**
 * As regras de tela da cota, puras — o componente só desenha o que sai daqui.
 *
 * A hierarquia é a do `/usage` do Claude Code, medida em 2026-09-23 (Claude
 * Code 2.1.281): janela de 5 h, semana de todos os modelos, semana por
 * modelo e, por último, o uso extra. Copia-se a informação e a ordem, não o
 * visual de terminal.
 */

/** Onde a barra muda de cor — e de palavra: cor sozinha não diz nada a quem não a distingue. */
export const LIMIAR_ATENCAO = 0.7;
export const LIMIAR_CRITICO = 0.9;

export type NivelDaCota = 'normal' | 'atencao' | 'critico';

export function nivelDaCota(utilizacao: number): NivelDaCota {
  if (utilizacao >= LIMIAR_CRITICO) return 'critico';
  if (utilizacao >= LIMIAR_ATENCAO) return 'atencao';
  return 'normal';
}

/** A palavra que acompanha a cor. O normal não ganha palavra: seria ruído em toda barra. */
export const PALAVRA_DO_NIVEL: Record<NivelDaCota, string | null> = {
  normal: null,
  atencao: 'atenção',
  critico: 'crítico',
};

/**
 * Acima disto cada mensagem relê um contexto grande.
 *
 * É o mesmo corte que o `/usage` usa no diagnóstico ("usage was at >150k
 * context") e o mesmo da autocompactação (`COMPACTAR_MIN_TOKENS`, ADR-0021):
 * três lugares dizendo o mesmo número, para o aviso da tela e o gesto do
 * daemon não discordarem.
 */
export const CONTEXTO_GRANDE = 150_000;

export function contextoGrande(tokens: number | null | undefined): boolean {
  return typeof tokens === 'number' && tokens > CONTEXTO_GRANDE;
}

/**
 * A cota só chega quando uma sessão daquele motor roda: passado disto sem
 * leitura, o número na tela é retrato antigo, e a tela diz.
 */
export const DADO_VELHO_MS = 30 * 60_000;

export function dadoVelho(atualizadoEm: string | null, agora = Date.now()): boolean {
  if (!atualizadoEm) return false;
  const lido = Date.parse(atualizadoEm);
  return !Number.isNaN(lido) && agora - lido > DADO_VELHO_MS;
}

const MINUTOS_NA_SEMANA = 7 * 24 * 60;

/** O nome da janela pela duração: `300` → "5 horas", `10080` → "semana". */
export function rotuloDaDuracao(duracaoMin: number | null): string | null {
  if (duracaoMin === null || duracaoMin <= 0) return null;
  if (duracaoMin === MINUTOS_NA_SEMANA) return 'semana';
  if (duracaoMin % 1_440 === 0) return `${duracaoMin / 1_440} dias`;
  if (duracaoMin % 60 === 0) return `${duracaoMin / 60} horas`;
  return `${duracaoMin} min`;
}

function comMaiuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

const PREFIXO_POR_MODELO = 'claude:modelo:';

/**
 * O uso extra, como janela (PLAN.md, Interface congelada 6): sem reinício e
 * sem duração, e só presente quando está ligado — o `/usage` o põe por último.
 */
const JANELA_DE_USO_EXTRA = 'claude:extra';

/**
 * O que aparece em cima do medidor.
 *
 * As janelas semanais do Claude levam o escopo, como no `/usage` ("Current
 * week (all models)", "Current week (Fable)"). As outras — as do Codex
 * inclusive — são nomeadas pela DURAÇÃO que a conta informa; o rótulo que o
 * servidor mandou só entra quando não há duração, porque aí é tudo o que se
 * sabe da janela.
 */
export function rotuloDaJanela(janela: JanelaDoPainel): string {
  const duracao = rotuloDaDuracao(janela.duracaoMin);
  if (janela.id === 'claude:7d') return 'Semana · todos os modelos';
  if (janela.id === 'claude:7d-opus') return 'Semana · Opus';
  if (janela.id === 'claude:7d-sonnet') return 'Semana · Sonnet';
  if (janela.id === JANELA_DE_USO_EXTRA) return 'Uso extra';
  // A janela por modelo vem da chamada experimental sem duração: o `/usage`
  // a chama de "Current week (<modelo>)", e é o que ela é.
  if (janela.id.startsWith(PREFIXO_POR_MODELO)) {
    return `${comMaiuscula(duracao ?? 'semana')} · ${janela.rotulo}`;
  }
  return duracao ? comMaiuscula(duracao) : janela.rotulo;
}

/** A posição de cada janela na ordem do `/usage`; o que não é do Claude vem depois. */
function posicaoDaJanela(janela: JanelaDoPainel): number {
  if (janela.id === 'claude:5h') return 0;
  if (janela.id === 'claude:7d') return 1;
  if (janela.id === 'claude:7d-opus') return 2;
  if (janela.id === 'claude:7d-sonnet') return 3;
  if (janela.id.startsWith(PREFIXO_POR_MODELO)) return 4;
  if (janela.id === JANELA_DE_USO_EXTRA) return 5;
  return 6;
}

/**
 * A ordem do `/usage`: 5 h, semana, semana por modelo, uso extra. Entre as que não têm
 * lugar fixo (as do Codex), a mais curta primeiro — é a que reinicia antes.
 */
export function ordenarJanelas(janelas: readonly JanelaDoPainel[]): JanelaDoPainel[] {
  return [...janelas].sort(
    (a, b) =>
      posicaoDaJanela(a) - posicaoDaJanela(b) ||
      (a.duracaoMin ?? Number.MAX_SAFE_INTEGER) - (b.duracaoMin ?? Number.MAX_SAFE_INTEGER),
  );
}

/** Contexto atual sobre o limite, quando o limite é conhecido: `612 mil / 1 mi`. */
export function contextoEmTexto(tokens: number | null, limite: number | null): string | null {
  if (tokens === null) return null;
  return limite === null ? tokensCurtos(tokens) : `${tokensCurtos(tokens)} / ${tokensCurtos(limite)}`;
}

/** Dólar do preço de API. Nunca fatura: a assinatura não cobra por token. */
export function custoEmTexto(usd: number | null): string | null {
  if (usd === null) return null;
  return usd.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' });
}

/**
 * O que a conta de cada motor tem para mostrar (D20, ADR-0022).
 *
 * Claude e Codex têm COTA de assinatura: o % da semana da conta, rateado entre
 * as sessões pelo peso. O OpenCode não tem cota RATEADA: no OpenRouter ele
 * cobra por token na chave, e o que se sabe é o custo REAL que ele relata a
 * cada turno (conferido contra o `usage` da chave, OC-27); no NanoGPT (F2) a
 * assinatura é da CONTA, contada em tokens de entrada, e o custo por turno é o
 * equivalente em preço de tabela (D45). Então o cartão dele mostra totais em
 * US$ (e, no NanoGPT, a assinatura da conta), e a linha mostra o custo, sem %
 * e sem barra. Um "sem dado" ali diria que falta uma leitura que nunca vai
 * existir.
 *
 * **O OpenCode continua `custo-real` aqui com o NanoGPT, de propósito** (plano
 * da F2, ANCORAS §2): virar "motor de cota" mexeria no rateio, nas sugestões e
 * em `temCotaDeAssinatura`. A assinatura do NanoGPT é um TOTAL da conta, e
 * aparece no cartão dela — nunca um % por sessão.
 */
export type FormaDaConta = 'cota' | 'custo-real';

export function formaDaConta(motor: Motor): FormaDaConta {
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return 'cota';
    case 'OPENCODE':
      return 'custo-real';
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * O cartão da conta aparece mesmo sem leitura nos motores de cota (decisão do
 * dono: "uma conta por motor"). No OpenCode ele só aparece quando a API trouxe
 * a conta, e ela só a traz quando há o que dizer (total conhecido ou sessão na
 * semana). Assim, com o recurso desligado, a tela não ganha um cartão vazio.
 */
export function cartaoSempreVisivel(motor: Motor): boolean {
  return formaDaConta(motor) === 'cota';
}

/**
 * O nome do custo do OpenCode por provedor (F2, D45): no OpenRouter é o
 * COBRADO ("custo real", R6); no NanoGPT é o EQUIVALENTE de tabela — o
 * OpenCode relata o preço mesmo quando a assinatura cobre a chamada, que sai de
 * graça (medido em 26/10). Sem provedor conhecido, só "custo": afirmar "real"
 * sobre um equivalente seria a tela mentindo sobre dinheiro.
 */
export function rotuloDoCustoDoOpencode(provedor: ProvedorOpencode | null): string {
  if (provedor === null) return 'custo';
  switch (provedor) {
    case 'nanogpt':
      return 'equivalente';
    case 'openrouter':
      return 'custo real';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/** O `title` do custo de uma sessão do OpenCode no cabeçalho da conversa: o que o número soma. */
export function explicacaoDoCustoDaSessao(provedor: ProvedorOpencode): string {
  switch (provedor) {
    case 'nanogpt':
      return 'Equivalente em preço de tabela, somado dos turnos desta semana — não é o que foi cobrado.';
    case 'openrouter':
      return 'O que o OpenRouter cobrou nesta semana, somado dos turnos.';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/**
 * O rótulo da coluna de custo de uma sessão: nos motores de cota é o preço de
 * API equivalente ("custo"); no OpenCode, o do provedor do MODELO dela — uma
 * conversa antiga do OpenRouter continua com o custo real dela mesmo com o
 * NanoGPT ativo.
 */
export function rotuloDoCusto(motor: Motor, modelo?: string | null): string {
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return 'custo';
    case 'OPENCODE':
      return rotuloDoCustoDoOpencode(modelo ? provedorDoModelo(modelo) : null);
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * O que a coluna "% da semana" diz de uma sessão do OpenCode, que não tem
 * rateio: no OpenRouter, que ela paga por uso; no NanoGPT, que ela gasta a
 * assinatura da CONTA e, depois, o crédito — o % mora no cartão da conta.
 */
export function textoSemCota(modelo: string | null): string {
  const provedor = modelo ? provedorDoModelo(modelo) : null;
  if (provedor === null) return 'sem cota por sessão';
  switch (provedor) {
    case 'nanogpt':
      return 'sem rateio · assinatura e crédito';
    case 'openrouter':
      return 'sem cota · paga por uso';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/**
 * A frase do selo "assinatura" de uma sessão do NanoGPT (D45), para o `title`
 * na lista e no cabeçalho — ou `null` quando o selo não se aplica.
 *
 * Só com `assinatura === true`: ausente é "não se aplica ou não se sabe" (o
 * catálogo não respondeu), e `false` é o modelo de fora da assinatura, que
 * gasta crédito — nenhum dos dois ganha o selo.
 */
export function descreverAssinaturaDaSessao(uso: UsoDaSessao | undefined): string | null {
  if (uso?.assinatura !== true) return null;
  const partes = ['Modelo incluído na assinatura do NanoGPT'];
  const custo = custoEmTexto(uso.custoEquivUsd);
  if (custo) partes.push(`equivalente de ${custo} em preço de tabela, não dinheiro`);
  const contexto = contextoEmTexto(uso.contextoTokens, uso.contextoLimite);
  if (contexto) partes.push(`contexto ${contexto}`);
  return partes.join(' · ');
}

/* -------------------------------------------------------------------------- */
/* A conta do NanoGPT no cartão do OpenCode (D43)                              */
/* -------------------------------------------------------------------------- */

/** O que o medidor da assinatura do NanoGPT desenha: a fração da semana, ou o porquê de não haver uma. */
export interface AssinaturaNaTela {
  /** Fração dos tokens de entrada da semana; `null` é "não sei". */
  utilizacao: number | null;
  reiniciaEm: string | null;
  /** A linha de baixo do medidor: "16,7 mil de 60 mi tokens de entrada", ou o motivo do "não sei". */
  detalhe: string;
}

/**
 * A assinatura do NanoGPT como o medidor a desenha (D43, P3 aprovado).
 *
 * - `degradada` é o `degraded: true` do NanoGPT: ele mesmo diz que o número
 *   não é confiável agora, e a tela diz "não sei" — mostrar 0% ali seria a cota
 *   cheia afirmada sem ninguém ter medido;
 * - sem assinatura ativa, tudo sai do crédito, e a frase diz isso;
 * - leitura que falhou (`null`) é "não consegui ler", nunca zero.
 *
 * `null` quando não há o que desenhar como medidor (a leitura da conta inteira
 * faltou): aí o cartão diz isso numa frase só.
 */
export function assinaturaNaTela(conta: ContaNanogpt | undefined): AssinaturaNaTela | null {
  if (!conta) return null;
  const a = conta.assinatura;
  if (a === null) {
    return { utilizacao: null, reiniciaEm: null, detalhe: 'não deu para ler a assinatura agora: não sei' };
  }
  if (!a.ativa) {
    return { utilizacao: null, reiniciaEm: null, detalhe: 'sem assinatura ativa: toda chamada gasta crédito' };
  }
  if (a.degradada) {
    return {
      utilizacao: null,
      reiniciaEm: a.reiniciaEm,
      detalhe: 'o NanoGPT avisou que a contagem está instável agora: não sei',
    };
  }
  if (a.usadosSemana === null || a.limiteSemana === null) {
    return { utilizacao: null, reiniciaEm: a.reiniciaEm, detalhe: 'o NanoGPT não disse quanto foi usado: não sei' };
  }
  return {
    // Acima de 100% é possível (a contagem do NanoGPT passa do limite antes de
    // cortar); a barra satura, o número não mente.
    utilizacao: a.usadosSemana / a.limiteSemana,
    reiniciaEm: a.reiniciaEm,
    detalhe: `${tokensCurtos(a.usadosSemana)} de ${tokensCurtos(a.limiteSemana)} tokens de entrada`,
  };
}

/** O título do cartão do OpenCode: o motor e, quando a API diz, o provedor ativo. */
export function tituloDoOpencode(provedor: ProvedorOpencode | undefined): string {
  return provedor ? `OpenCode · ${nomeDoProvedorOpencode(provedor)}` : 'OpenCode';
}

/**
 * O que ordena as sessões DENTRO do mesmo motor: o peso do rateio na cota, ou,
 * no OpenCode (peso zero, sem cota), o custo real.
 */
export function pesoNaOrdem(sessao: Pick<SessaoDoPainel, 'motor' | 'peso' | 'custoEquivUsd'>): number {
  return formaDaConta(sessao.motor) === 'custo-real' ? (sessao.custoEquivUsd ?? 0) : sessao.peso;
}

/** Abaixo disto o selo da lista não aparece: marcar tudo com "<1%" vira textura. */
export const SELO_MINIMO = 0.01;

/**
 * A frase que explica o selo da lista, para o `title`.
 *
 * `null` quando não há selo: sem cota conhecida, ou abaixo de 1%.
 */
export function descreverUsoDaSessao(uso: UsoDaSessao | undefined): string | null {
  if (!uso || uso.pctSemanal === null || uso.pctSemanal < SELO_MINIMO) return null;
  const conta = uso.email ? `da conta ${uso.email}` : 'da conta';
  const partes = [`${porcento(uso.pctSemanal)} da cota semanal ${conta} (estimativa por rateio)`];
  const custo = custoEmTexto(uso.custoEquivUsd);
  if (custo) partes.push(`${custo} em preço de API`);
  const contexto = contextoEmTexto(uso.contextoTokens, uso.contextoLimite);
  if (contexto) partes.push(`contexto ${contexto}`);
  return partes.join(' · ');
}
